import { ArrowDropDown, ArrowRight } from '@mui/icons-material';
import { Box, Button, Table, TableBody } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncContent } from '~/components/AsyncContent';
import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { ApprovalRowMode } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/components/ApprovalWidgetContent/components/CategoryListItem/components/ApprovalRow/component';
import {
  ApprovalWidgetCategory,
  BulkApprovalAction,
} from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/data';
import { ApprovalWidgetData } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/types';
import { UseApprovalWidgetDataOutput } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/useApprovalWidgetData';

import { ApprovalRow } from './components/ApprovalRow';
import { TableHeader } from './components/TableHeader';
import { useSxStyles } from './useStyles';

type CategoryListItemProps = {
  data: ApprovalWidgetData[];
  category: ApprovalWidgetCategory;
  isOpened: boolean;
  categoryName: string;
  categoryThirdValue: (widgetData: ApprovalWidgetData) => string;
  showActionDetails: (widgetData: ApprovalWidgetData) => string;
  secondCellName: string;
  thirdCellName: string;
  showEndDate?: boolean;
  isLoading: boolean;
  failedToLoad: boolean;
  noData: boolean;
  rowMode: ApprovalRowMode;
  onToggleOpen: () => void;
  selection: ApprovalWidgetData[];
  onSelection: UseApprovalWidgetDataOutput['setSelection'];
  onDecision: UseApprovalWidgetDataOutput['onDecision'];
};

export const CategoryListItem = (props: CategoryListItemProps) => {
  const {
    data,
    isOpened,
    category,
    categoryName,
    categoryThirdValue,
    showActionDetails,
    secondCellName,
    thirdCellName,
    showEndDate,
    isLoading,
    failedToLoad,
    noData,
    rowMode,

    selection,
    onSelection,
    onDecision,
    onToggleOpen,
  } = props;

  const sxStyles = useSxStyles();
  const { t } = useTranslation();
  const areAllItemsSelected = data.length === selection.length;
  const isAnythingSelected = !!selection.length;

  const handleSelection = useCallback(
    (selection: ApprovalWidgetData[]) => {
      onSelection(category, selection);
    },
    [category, onSelection],
  );

  const handleSelectAllChange = useCallback(
    (isAllSelected: boolean) => {
      handleSelection(isAllSelected ? data : []);
    },
    [handleSelection, data],
  );

  const handleDecision = useCallback(
    (decision: BulkApprovalAction) => {
      onDecision(decision, category);
    },
    [category, onDecision],
  );

  return (
    <>
      <Box sx={sxStyles.categoryHeader}>
        <Box sx={sxStyles.categoryHeaderRow}>
          <Box
            component="p"
            sx={sxStyles.expandableIcon}
            onClick={onToggleOpen}
          >
            {isOpened ? <ArrowDropDown /> : <ArrowRight />}
          </Box>
          <TooltippedTruncatedText>
            <Box sx={sxStyles.actionScopes}>{categoryName}</Box>
          </TooltippedTruncatedText>
          <Box sx={{ ...sxStyles.actionScopes, ...sxStyles.categoryValue }}>
            {`(${selection.length}/${data.length})`}
          </Box>
        </Box>
        {data.length > 0 && (
          <Box sx={sxStyles.categoryHeaderRow}>
            <Button
              color="primary"
              size={'small'}
              variant="contained"
              disabled={!isAnythingSelected || failedToLoad || noData}
              sx={{
                ...sxStyles.categoryButton,
                ...sxStyles.approveButton,
              }}
              onClick={() => handleDecision(BulkApprovalAction.APPROVE)}
            >
              {t('approve')}
            </Button>
            <Button
              color="primary"
              size={'small'}
              variant="contained"
              disabled={!isAnythingSelected || failedToLoad || noData}
              sx={{
                ...sxStyles.categoryButton,
                ...sxStyles.rejectButton,
              }}
              onClick={() => handleDecision(BulkApprovalAction.REJECT)}
            >
              {t('reject')}
            </Button>
          </Box>
        )}
      </Box>
      {isOpened && (
        <AsyncContent
          isLoading={isLoading}
          failedToLoad={failedToLoad}
          noData={noData}
          spinnerSize={20}
        >
          <Table sx={sxStyles.table}>
            <TableHeader
              allSelected={areAllItemsSelected}
              onSelectAllChange={handleSelectAllChange}
              secondCellName={secondCellName}
              thirdCellName={thirdCellName}
              tableMode={rowMode === ApprovalRowMode.Table}
            />
            <TableBody>
              {data
                .filter((widgetData: ApprovalWidgetData) => widgetData.employee)
                .map((widgetData: ApprovalWidgetData) => (
                  <ApprovalRow
                    key={`${widgetData.employee.id}-${widgetData.startDate}-${widgetData.id}`}
                    data={widgetData}
                    actionDetails={showActionDetails(widgetData)}
                    category={category}
                    showEndDate={showEndDate}
                    categoryThirdValue={categoryThirdValue}
                    rowMode={rowMode}
                    secondCellName={secondCellName}
                    thirdCellName={thirdCellName}
                    selection={selection}
                    onSelection={handleSelection}
                  />
                ))}
            </TableBody>
          </Table>
        </AsyncContent>
      )}
    </>
  );
};
